"use client";
import React from "react";
import { motion } from "motion/react";
import { useTheme } from "next-themes";
import { ProjectType } from "@/components/utils/types";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  CodeIcon,
  Database01Icon,
  Configuration02Icon,
  ActivityIcon,
  CloudIcon,
  TickDouble01Icon,
  InternetIcon,
  SmartPhone01Icon,
} from "@hugeicons/core-free-icons";
import {
  NextjsIcon,
  TypescriptIcon,
  TailwindcssIcon,
  FramerIcon,
  FirebaseIcon,
  ZustandIcon,
  ZodIcon,
  NodejsIcon,
  AxiosIcon,
  ImagekitioIcon,
  TiptapIcon,
  RazorpayIcon,
  GeminiIcon,
} from "@/components/icons";
import type { IconSvgElement } from "@hugeicons/react";

interface TechStackProps {
  project: ProjectType;
}

export const TechStack = ({ project }: TechStackProps) => {
  const { resolvedTheme } = useTheme();

  const renderTechIcon = (icon: string | IconSvgElement, className?: string) => {
    if (typeof icon !== "string") {
      return <HugeiconsIcon icon={icon} className={className} />;
    }

    const lowerName = icon.toLowerCase();

    // 1. Custom Brand SVGs
    switch (lowerName) {
      case "nextjs":
      case "nextjs 15":
      case "nextjs 16":
        return <NextjsIcon className={className} />;
      case "typescript":
        return <TypescriptIcon className={className} />;
      case "tailwind":
      case "tailwindcss":
      case "tailwind css":
      case "tailwind css 4":
        return <TailwindcssIcon className={className} />;
      case "framer":
      case "framer-motion":
      case "motion":
        return <FramerIcon className={`${className} ${resolvedTheme === "dark" ? "text-white" : "text-black"}`} />;
      case "firebase":
        return <FirebaseIcon className={className} />;
      case "zustand":
        return <ZustandIcon className={className} />;
      case "zod":
        return <ZodIcon className={className} />;
      case "nodejs":
        return <NodejsIcon className={className} />;
      case "axios":
        return <AxiosIcon className={className} />;
      case "imagekit":
      case "imagekit.io":
        return <ImagekitioIcon className={className} />;
      case "tiptap":
      case "tiptap rich-text editor":
        return <TiptapIcon className={className} />;
      case "razorpay":
        return <RazorpayIcon className={className} />;
      case "gemini":
      case "gemini api":
      case "google gemini api":
      case "google":
      case "google genkit":
        return <GeminiIcon className={className} />;
    }

    // 2. Hugeicons fallbacks for other technologies
    let hugeIcon = CodeIcon;
    if (lowerName.includes("database") || lowerName.includes("sql") || lowerName.includes("mongo")) {
      hugeIcon = Database01Icon;
    } else if (lowerName.includes("api") || lowerName.includes("rest")) {
      hugeIcon = InternetIcon;
    } else if (lowerName.includes("config") || lowerName.includes("setup")) {
      hugeIcon = Configuration02Icon;
    } else if (lowerName.includes("motion") || lowerName.includes("animation")) {
      hugeIcon = ActivityIcon;
    } else if (lowerName.includes("cloud") || lowerName.includes("serverless")) {
      hugeIcon = CloudIcon;
    } else if (lowerName.includes("validation") || lowerName.includes("auth")) {
      hugeIcon = TickDouble01Icon;
    } else if (lowerName.includes("mobile") || lowerName.includes("phone")) {
      hugeIcon = SmartPhone01Icon;
    }

    return <HugeiconsIcon icon={hugeIcon} className={className} />;
  };

  return (
    <section className="min-w-full min-h-[60vh] gap-4 flex flex-col items-center justify-center">
      {/* Section Header */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ type: "spring", stiffness: 100, damping: 20 }}
        className="flex flex-col items-center justify-center text-center mb-8"
      >
        <h2 className="font-recoleta text-4xl md:text-6xl lg:text-7xl text-black dark:text-white mb-2">
          Tech{" "}
          <span className="bg-linear-to-r from-sky-500 to-blue-600 bg-clip-text text-transparent">
            Stack
          </span>
        </h2>
      </motion.div>

      {/* Tech Stack Grid */}
      <div className="flex flex-wrap px-2 md:px-40 justify-center items-center gap-4">
        {project.techIcons?.map((t, i) => (
          <motion.div
            key={t.name + i}
            initial={{ opacity: 0, y: 30, scale: 0.9 }}
            whileInView={{ opacity: 1, y: 0, scale: 1 }}
            transition={{
              type: "spring",
              stiffness: 100,
              damping: 15,
              delay: i * 0.08,
            }}
            whileHover={{
              y: -8,
              scale: 1.02,
              transition: { type: "spring", stiffness: 300, damping: 20 },
            }}
            className="group relative w-28 h-28 md:w-36 md:h-36 shrink-0 rounded-2xl md:rounded-3xl cursor-pointer"
          >
            {/* Glass Card Background */}
            <div className="absolute inset-0 rounded-2xl md:rounded-3xl bg-white/70 dark:bg-white/5 backdrop-blur-xl border border-white/50 dark:border-white/10 shadow-lg shadow-black/5 dark:shadow-black/20 group-hover:bg-white/90 dark:group-hover:bg-white/10 group-hover:border-sky-500/20 dark:group-hover:border-sky-400/20 group-hover:shadow-xl group-hover:shadow-sky-500/10 transition-all duration-500" />

            {/* Hover Glow Effect */}
            <motion.div
              initial={{ opacity: 0 }}
              whileHover={{ opacity: 1 }}
              className="absolute inset-0 rounded-2xl md:rounded-3xl bg-linear-to-br from-sky-500/5 via-transparent to-blue-500/5 opacity-0 group-hover:opacity-100 transition-opacity duration-500"
            />

            {/* Content */}
            <div className="relative z-10 h-full flex flex-col items-center justify-center gap-2.5 md:gap-3 p-4">
              {/* Icon Container */}
              <motion.div
                whileHover={{ rotate: [0, -5, 5, 0] }}
                transition={{ duration: 0.4 }}
                className="relative"
              >
                <div className="p-2.5 md:p-3 bg-white dark:bg-zinc-800/80 rounded-xl md:rounded-2xl shadow-sm border border-black/5 dark:border-white/10 group-hover:shadow-md group-hover:scale-110 transition-all duration-300">
                  {t.icon && renderTechIcon(t.icon, "w-8 h-8 md:w-10 md:h-10")}
                </div>

                {/* Subtle Ring Animation on Hover */}
                <motion.div
                  initial={{ scale: 0.8, opacity: 0 }}
                  whileHover={{ scale: 1.2, opacity: 0.3 }}
                  className="absolute inset-0 rounded-xl md:rounded-2xl border-2 border-sky-500 opacity-0 group-hover:opacity-30 transition-opacity duration-300"
                />
              </motion.div>

              {/* Tech Name */}
              <motion.span
                initial={{ opacity: 0.6 }}
                whileHover={{ opacity: 1 }}
                className="text-xs md:text-sm font-medium font-outfit text-black/60 dark:text-white/60 group-hover:text-black dark:group-hover:text-white text-center leading-tight transition-colors duration-300"
              >
                {t.name}
              </motion.span>
            </div>

            {/* Bottom Accent Line */}
            <motion.div
              initial={{ scaleX: 0 }}
              whileHover={{ scaleX: 1 }}
              transition={{ duration: 0.3, ease: "easeOut" }}
              className="absolute bottom-0 left-1/2 -translate-x-1/2 w-1/2 h-0.5 bg-linear-to-r from-sky-500 to-blue-500 rounded-full origin-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"
            />
          </motion.div>
        ))}
      </div>
    </section>
  );
};
